import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import { isNull, isUndefined } from 'lodash'
import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'
import uniq from 'lodash/uniq'
import moment from 'moment'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import { dateBackendDateTimeToUI, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import usePrompt from 'modules/core/lib/react-router/usePrompt'

import CustomFields from 'modules/custom_resources/store/collections/customFields/fields'
import CustomValues from 'modules/custom_resources/store/collections/customFields/values'
import type CustomField from 'modules/custom_resources/store/models/customFields/Field'
import { ChoiceOption } from 'modules/custom_resources/store/models/customFields/Field'
import ReviewCompetencyAssessment from 'modules/reviews/components/Competencies/ReviewCompetencyAssessment'
import useCompetencyAssessments from 'modules/reviews/components/Competencies/ReviewCompetencyAssessment/useCompetencyAssessments'
import QuestionsWithCategory from 'modules/reviews/components/WriteResponse/QuestionsWithCategory'
import QuestionsWithoutCategory from 'modules/reviews/components/WriteResponse/QuestionsWithoutCategory'
import QuestionsWithoutCategoryWithScoreType from 'modules/reviews/components/WriteResponse/QuestionsWithoutCategoryWithScoreType'
import { useScore } from 'modules/reviews/hooks/useScore'
import { ScoreContext } from 'modules/reviews/lib/PerformanceScoreContext'
import coerceValueFromType from 'modules/reviews/lib/coerceValueFromType'
import type QuestionGroup from 'modules/reviews/store/models/QuestionGroup'

function missingMandatory(forms: Form[]): boolean {
  return Boolean(
    forms
      .filter((form) => Boolean(form.get('mandatory').value))
      .find((form) => {
        const value = form.get('value').value

        return isNull(value) || isUndefined(value) || value === ''
      })
  )
}

function isFormDirty(forms: Form[]): boolean {
  return forms.some((form) => form.isDirty)
}

function clearForms(forms: Form[]) {
  forms.map((form) => form.cleanAll())
}

interface Props {
  questionGroup: QuestionGroup
  onSubmit?: () => void
  onDraftSaved: () => Promise<void>
  canReview: boolean
  isReviewActive?: boolean
  assessCompetencies: boolean
}

function ResponseQuestions({
  questionGroup,
  onSubmit,
  onDraftSaved,
  canReview,
  isReviewActive,
  assessCompetencies,
}: Props) {
  const questionsWithCategory = questionGroup.withCategory
  const questionsWithoutCategoryWithoutType = questionGroup.withoutCategoryWithoutType
  const questionsWithoutCategoryWithScoreType = questionGroup.withoutCategoryWithScoreType
  const editableUntil = questionGroup.attributes.editable_until
  const answerToBeEditable =
    canReview && isReviewActive && (!editableUntil || moment().isBefore(editableUntil))

  const [editingSubmittedAnswer, setEditingSubmittedAnswer] = React.useState(false)
  const [isAnswerSubmittedOrDrafted, setIsAnswerSubmittedOrDrafted] = React.useState(false)
  const cantBeEdited =
    (questionGroup.isAnswered && !editingSubmittedAnswer) || questionGroup.isClosed

  const {
    competencyAssessments,
    updateCompetencyAssessment,
    competencyAssessmentsChanged,
    competencyAssessmentsAnswered,
    resetCompetencyAssessments,
  } = useCompetencyAssessments(questionGroup.competenciesAssessments)

  const reopenReview = () => {
    track(trackingEvents.reviews.editReview)
    setEditingSubmittedAnswer(true)
  }

  const filterByCategory = React.useRef(
    groupBy(questionsWithCategory, (q: any) => q.surveys_question_category_id)
  ).current

  const uniqueCategories = React.useRef(
    uniq(
      questionsWithCategory.map((q: any) => q.surveys_question_category_id).sort((a, b) => a - b)
    )
  ).current

  const createForm = (field: CustomField, value: any) => {
    const values = {
      field_id: field.attributes.id,
      field_type: field.attributes.field_type,
      mandatory: field.attributes.required,
      name: field.attributes.label,
      min_value: field.attributes.min_value,
      max_value: field.attributes.max_value,
      value,
    }

    return new Form(values, {
      field_id: 'number',
      field_type: 'string',
      mandatory: 'boolean',
      name: 'string',
      min_value: 'number',
      max_value: 'number',
      value: 'string',
    })
  }

  const onFormSubmittedOrDrafted = () => {
    clearForms(formsWithoutCategoryWithoutType)
    clearForms(formsWithoutCategoryWithScoreType)
    clearForms(formsWithCategory)
    resetCompetencyAssessments(questionGroup.competenciesAssessments)
    setIsAnswerSubmittedOrDrafted(false)
  }

  // Questions without category with null question_type
  const customFieldsWithoutCategoryWithoutType = React.useRef(
    new CustomFields(
      questionsWithoutCategoryWithoutType.map((payload: any) => {
        if (!payload.custom_field) return payload

        return { ...payload.custom_field, position: payload.position }
      }),
      {
        model_ref: 'surveys-question_group',
        instance_id: questionGroup.attributes.id,
      }
    )
  ).current

  const customValuesWithoutCategoryWithoutType = React.useRef(
    new CustomValues(questionGroup.attributes.answers, {
      model_ref: 'QuestionGroup',
      instance_id: questionGroup.attributes.id,
    })
  ).current

  const optionsWithoutCategoryWithoutType = React.useMemo(() => {
    return customFieldsWithoutCategoryWithoutType.models.reduce<Record<string, any>>((memo, cf) => {
      memo[cf.attributes.id] = cf.choiceOptions.filter((opt) => opt.isActive)

      return memo
    }, {})
  }, [customFieldsWithoutCategoryWithoutType])

  const formsWithoutCategoryWithoutType = React.useRef(
    sortBy(customFieldsWithoutCategoryWithoutType.models, (cf) => cf.attributes.position).map(
      (field) => {
        const answer = customValuesWithoutCategoryWithoutType.find({
          field_id: field.attributes.id,
        })

        const value = answer ? coerceValueFromType(field, answer.attributes.value) : ''

        return createForm(field, value)
      }
    )
  ).current

  // Questions without category with score question_type
  const customFieldsWithoutCategoryWithScoreType = React.useRef(
    new CustomFields(
      questionsWithoutCategoryWithScoreType.map((payload: any) => {
        if (!payload.custom_field) return payload

        return { ...payload.custom_field, position: payload.position }
      }),
      {
        model_ref: 'surveys-question_group',
        instance_id: questionGroup.attributes.id,
      }
    )
  ).current

  const customValuesWithoutCategoryWithScoreType = React.useRef(
    new CustomValues(questionGroup.attributes.answers, {
      model_ref: 'QuestionGroup',
      instance_id: questionGroup.attributes.id,
    })
  ).current

  const optionsWithoutCategoryWithScoreType = React.useMemo(() => {
    return customFieldsWithoutCategoryWithScoreType.models.reduce<Record<string, any>>(
      (memo, cf) => {
        memo[cf.attributes.id] = cf.choiceOptions.filter((opt) => opt.isActive)

        return memo
      },
      {}
    )
  }, [customFieldsWithoutCategoryWithScoreType])

  const formsWithoutCategoryWithScoreType = React.useRef(
    sortBy(customFieldsWithoutCategoryWithScoreType.models, (cf) => cf.attributes.position).map(
      (field) => {
        const answer = customValuesWithoutCategoryWithScoreType.find({
          field_id: field.attributes.id,
        })

        const value = answer ? coerceValueFromType(field, answer.attributes.value) : ''

        return createForm(field, value)
      }
    )
  ).current

  // Questions With category
  const customFieldsWithCategory = React.useRef(
    new CustomFields(
      questionsWithCategory.map((payload: any) => {
        if (!payload.custom_field) return payload

        return { ...payload.custom_field, position: payload.position }
      }),
      {
        model_ref: 'surveys-question_group',
        instance_id: questionGroup.attributes.id,
      }
    )
  ).current

  const customValuesWithCategory = React.useRef(
    new CustomValues(questionGroup.attributes.answers, {
      model_ref: 'QuestionGroup',
      instance_id: questionGroup.attributes.id,
    })
  ).current

  const optionsWithCategory = React.useMemo(() => {
    return customFieldsWithCategory.models.reduce<Record<string, ChoiceOption[]>>((memo, cf) => {
      memo[cf.get('id')] = cf.choiceOptions.filter((opt) => opt.isActive)

      return memo
    }, {})
  }, [customFieldsWithCategory])

  const formsWithCategory = React.useRef<Form[]>(
    sortBy(customFieldsWithCategory.models, (cf) => cf.get('position')).map((field) => {
      const answer = customValuesWithCategory.find({
        field_id: field.get('id'),
      })

      const value = answer ? coerceValueFromType(field, answer.attributes.value) : ''

      return createForm(field, value)
    })
  ).current

  const computeData = ({ isDraft = false } = {}) => {
    const data: any = {
      surveys_question_group_id: questionGroup.attributes.id,
      answers: {},
      is_draft: isDraft,
      competencies_assessments: competencyAssessments,
    }
    formsWithoutCategoryWithoutType.forEach((form) => {
      const fieldId = form.get('field_id').value
      const value = form.get('value').value

      if (value !== undefined) data.answers[fieldId] = value
    })
    formsWithoutCategoryWithScoreType.forEach((form) => {
      const fieldId = form.get('field_id').value
      const value = form.get('value').value

      if (value !== undefined) data.answers[fieldId] = value
    })
    formsWithCategory.forEach((form) => {
      const fieldId = form.get('field_id').value
      const value = form.get('value').value

      if (value !== undefined) data.answers[fieldId] = value
    })

    return data
  }

  const {
    success: successSubmit,
    value: valueSubmit,
    running: runningSubmit,
    performAction: onSubmitAnswer,
  } = useAsyncAction(async () => {
    setIsAnswerSubmittedOrDrafted(true)

    const data = computeData()
    const result = await questionGroup.rpc('create_values', data)

    const answeredAtData = {
      answered_at: dateToString(moment(), 'backend'),
    }
    const newAssessments = {
      competencies_assessments: result.competencies_assessments,
    }
    questionGroup.set({ ...answeredAtData, ...newAssessments })
    onFormSubmittedOrDrafted()
    onSubmit && onSubmit()
    track(trackingEvents.reviews.publishReview, {
      is_successful: successSubmit,
      error_code: !successSubmit && JSON.stringify(valueSubmit),
    })
    if (questionGroup.competenciesAssessments.length || competencyAssessments.length) {
      track(trackingEvents.reviews.submitFeedbackWithCompetencies)
    }
  })

  const {
    success: successDraft,
    value: valueDraft,
    running: runningDraft,
    performAction: onSaveDraft,
  } = useAsyncAction(
    async () => {
      const data = computeData({
        isDraft: true,
      })
      const result = await questionGroup.rpc('create_values', data)
      setIsAnswerSubmittedOrDrafted(true)

      const draftedAt = {
        drafted_at: dateToString(moment(), 'backend'),
      }
      const newAssessments = {
        competencies_assessments: result.competencies_assessments,
      }
      questionGroup.set({ ...draftedAt, ...newAssessments })
      onFormSubmittedOrDrafted()
      onDraftSaved && onDraftSaved()
      track(trackingEvents.reviews.saveReviewDraft, {
        is_successful: successDraft,
        error_code: !successDraft && JSON.stringify(valueDraft),
      })
    },
    {
      successMessage: i18n.t('common.draft_saved'),
    }
  )

  const time = stringToDate(
    dateBackendDateTimeToUI(questionGroup.attributes.answered_at),
    'ui'
  ).format('L')

  const isAnyFormDirty =
    isFormDirty(formsWithoutCategoryWithoutType) ||
    isFormDirty(formsWithoutCategoryWithScoreType) ||
    isFormDirty(formsWithCategory) ||
    competencyAssessmentsChanged

  usePrompt(
    i18n.t('common.confirm_navigation'),
    () => isAnyFormDirty && !isAnswerSubmittedOrDrafted
  )

  const { setScore } = React.useContext(ScoreContext)
  const { calculateScore } = useScore()
  setScore(calculateScore(formsWithCategory.concat(formsWithoutCategoryWithoutType)))

  return (
    <>
      {!!formsWithoutCategoryWithoutType.length && (
        <Box gap="s48" padding="s24">
          {formsWithoutCategoryWithoutType.map((form: Form, index: number) => {
            const field = customFieldsWithoutCategoryWithoutType.get(
              Number(form.get('field_id').value)
            )

            if (!field) {
              return null
            }

            return (
              <QuestionsWithoutCategory
                canReview={canReview}
                key={index}
                form={form}
                isResponseValues={cantBeEdited}
                options={optionsWithoutCategoryWithoutType}
                field={field}
              />
            )
          })}
        </Box>
      )}
      {uniqueCategories &&
        uniqueCategories.map((categoryId) => {
          const firstFilter: any = filterByCategory[categoryId]?.[0]

          return (
            <QuestionsWithCategory
              canReview={canReview}
              key={firstFilter.surveys_question_category.name}
              categoryName={firstFilter.surveys_question_category.name}
              questionCategories={filterByCategory[categoryId] ?? []}
              formsWithCategory={formsWithCategory}
              optionsWithCategory={optionsWithCategory}
              isResponseValues={cantBeEdited}
            />
          )
        })}

      {assessCompetencies && (
        <ReviewCompetencyAssessment
          assessments={competencyAssessments}
          readonly={cantBeEdited || !canReview}
          onUpdate={updateCompetencyAssessment}
          accessCompetencies={assessCompetencies}
        />
      )}

      {!!formsWithoutCategoryWithScoreType.length && (
        <Box gap="s48">
          <QuestionsWithoutCategoryWithScoreType
            formsWithoutCategoryWithScoreType={formsWithoutCategoryWithScoreType}
            customFieldsWithoutCategoryWithScoreType={customFieldsWithoutCategoryWithScoreType}
            optionsWithoutCategoryWithScoreType={optionsWithoutCategoryWithScoreType}
            isResponseValues={
              (questionGroup.isAnswered && !editingSubmittedAnswer) || questionGroup.isClosed
            }
            canReview={canReview}
          />
        </Box>
      )}
      {/* 1 - "Show Draft and Submit" */}
      {answerToBeEditable &&
        (questionGroup.isDraft || questionGroup.isActive || editingSubmittedAnswer) && (
          <Box justifyContent="flexEnd" flexDirection="row" flexGrow padding="s24" gap="s16">
            <Button
              type="secondary"
              onClick={onSaveDraft}
              disabled={
                runningDraft || runningSubmit || !isAnyFormDirty || !competencyAssessmentsAnswered
              }
            >
              {questionGroup.isDraft
                ? i18n.t('surveys.form.response.update_draft')
                : i18n.t('surveys.form.response.save_draft')}
            </Button>
            <Button
              onClick={onSubmitAnswer}
              disabled={
                runningDraft ||
                runningSubmit ||
                missingMandatory(formsWithoutCategoryWithoutType) ||
                missingMandatory(formsWithoutCategoryWithScoreType) ||
                missingMandatory(formsWithCategory) ||
                !competencyAssessmentsAnswered
              }
            >
              {i18n.t('surveys.form.response.submit')}
            </Button>
          </Box>
        )}
      {/* 2 - "Show Edit feedback" */}
      {answerToBeEditable && !editingSubmittedAnswer && questionGroup.isAnswered && (
        <Box
          justifyContent="spaceBetween"
          flexDirection="row"
          flexGrow
          padding="s24"
          gap="s16"
          alignItems="center"
        >
          <Box>
            {i18n.t('surveys.form.response.published')} {time}
          </Box>
          <Button type="secondary" onClick={() => reopenReview()}>
            {i18n.t('surveys.form.response.edit')}
          </Button>
        </Box>
      )}
    </>
  )
}

export default observer(ResponseQuestions)
